import {useMemo} from 'react'
import includes from 'lodash/includes'
import {exhaustive} from '@freckle/exhaustive'
import type {LangT} from '@freckle/educator-entities/ts/common/helpers/languages'
import {fetchLanguages} from '@freckle/educator-entities/ts/common/models/language'
import {SelfContainedTeacherSelector} from '@freckle/educator-materials/ts/components/self-contained-teacher-selector'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {groupedOptions} from '@freckle/educator-materials/ts/logic/dropdown'
import {patchTeacher} from '@freckle/educator-materials/ts/logic/self-contained-teacher-selector'

export type UpdateModeT =
  | {
      tag: 'component-performs-update'
      teacherId: number
    }
  | {
      tag: 'component-is-passthrough'
    }

type ComponentLabelsT = {
  formLabel: string | null
  loadingText: string
}

type Language = {
  language: LangT
  name: string
}

export type Props = {
  updateMode: UpdateModeT
  labels: ComponentLabelsT
  onError?: (error: Error) => void
  selectedLanguage: LangT
  // The language from which to view the language list
  perspectiveLanguage: LangT
  selectorClassName?: string | null
  dataTest?: string
  filterLanguages?: LangT[]
  onChange: (language: LangT) => void
}

export function LanguageDropdown({
  selectedLanguage: selectedItem,
  perspectiveLanguage,
  onChange: onChangeSelection,
  updateMode,
  labels,
  onError,
  dataTest,
  selectorClassName,
  filterLanguages
}: Props) {
  const onChangeSelectionP = (selectedSetting: LangT) => {
    switch (updateMode.tag) {
      case 'component-is-passthrough':
        return Promise.resolve(onChangeSelection(selectedSetting))
      case 'component-performs-update':
        return setTeacherLanguage(updateMode.teacherId, selectedSetting).then(() =>
          onChangeSelection(selectedSetting)
        )
      default:
        return exhaustive((updateMode as {tag: never}).tag, 'UpdateModeT')
    }
  }

  const fetchObj = useMemo(
    () => ({tag: 'be-fetched' as const, fetch: () => fetchLanguages(perspectiveLanguage)}),
    [perspectiveLanguage]
  )

  const {t} = useSafeTranslation()

  const itemsToCollection = (languages: Language[]) => {
    const invalidOptions = filterLanguages ?? []

    const validLanguageOptions = languages.filter(
      (language: Language) => !includes(invalidOptions, language.language)
    )

    const languageOptions = validLanguageOptions.map(option => ({
      label: option.name,
      value: option.language,
      dataTest: `language-option-${option.language}`
    }))
    const label = t('TEACHER_LANGUAGE_DROPDOWN_LANGUAGE_SETTING_APPLICATION_INFO')

    return groupedOptions([{label, options: languageOptions}])
  }

  return (
    <SelfContainedTeacherSelector
      id="teacher-language-selector"
      labels={labels}
      selectedItem={selectedItem}
      itemsToCollection={itemsToCollection}
      onChangeSelection={onChangeSelectionP}
      dataTest={dataTest}
      fetchItems={fetchObj}
      selectorClassName={selectorClassName}
      onFetchError={onError}
    />
  )
}

const setTeacherLanguage = (teacherId: number, language: LangT): Promise<unknown> =>
  patchTeacher(teacherId, {language})
